import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { screeningApi } from '../api/case-file.api.js';
import { customersApi, type CompanyScreening } from '../api/customers.api.js';
import { CompanyScreeningReportButton } from './CompanyScreeningReportButton.js';
import { APP_LOCALE } from '../utils/locale.js';

/**
 * The company's own sanctions screening (DEV-89).
 *
 * A business application screens the company and each of its people in one
 * go. The people's results had a place on screen; the company's did not, so a
 * reviewer saw the director's identity check and nothing about the company.
 * This shows the company's result, when it was screened, and whether its
 * report is on the client file — filed as its own document type, apart from
 * the UBO and identity reports.
 */

const STATE: Record<NonNullable<CompanyScreening['state']>, { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info' }> = {
  CLEAR: { label: 'Clear — no matches', color: 'success' },
  MATCHES_FOUND: { label: 'Possible matches', color: 'warning' },
  IN_PROGRESS: { label: 'Screening in progress', color: 'info' },
  NOT_STARTED: { label: 'Not started', color: 'default' },
  FAILED: { label: 'Screening failed', color: 'error' },
};

const FAILURE: Record<string, string> = {
  EXISTING_REQUEST: 'The provider already had an open request for this company and did not start a new one.',
  SUBMISSION_REJECTED: 'The provider refused the screening request.',
};

interface CompanyScreeningPanelProps {
  customerId: string;
  /** Compliance and admin may start a screening and file the report on demand. */
  canFile: boolean;
}

export function CompanyScreeningPanel({ customerId, canFile }: CompanyScreeningPanelProps): JSX.Element {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const screening = useQuery({
    queryKey: ['company-screening', customerId],
    queryFn: () => customersApi.companyScreening(customerId),
  });
  const file = useMutation({
    mutationFn: () => customersApi.fileCompanyScreeningReport(customerId),
    onSuccess: ({ outcome, screening: next }) => {
      queryClient.setQueryData(['company-screening', customerId], next);
      void queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
      setMessage(
        outcome === 'FILED'
          ? 'Report filed in the client’s documents.'
          : outcome === 'ALREADY_FILED'
            ? 'This screening’s report is already on file.'
            : outcome === 'NOT_COMPLETED'
              ? 'The screening has not finished yet, so there is no report to file.'
              : 'The provider could not build the report right now. It will be filed automatically once it can.',
      );
    },
    onError: () => setMessage('The report could not be filed. Try again shortly.'),
  });
  // Screens the company together with its UBOs and directors, as submission
  // and an approved identity check do (DEV-89).
  const screen = useMutation({
    mutationFn: () => screeningApi.rescreen(customerId, 'Company screening started from the application'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['company-screening', customerId] });
      setMessage('Screening started. The result appears here when the provider answers — usually within a few minutes.');
    },
    onError: () => setMessage('The screening could not be started. Try again shortly.'),
  });

  const data = screening.data;
  const state = data?.state ?? null;
  const shown = state === null ? null : STATE[state];

  return (
    <Paper variant="outlined" component="section" aria-labelledby="company-screening-title" sx={{ p: 2.5, minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography id="company-screening-title" component="h2" variant="subtitle2">Company screening</Typography>
          <Typography variant="body2" color="text.secondary">
            Sanctions and watchlist screening of the company itself, apart from its UBOs and directors.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {canFile ? (
            <Button size="small" variant="contained" disabled={screen.isPending || data?.state === 'IN_PROGRESS'}
              onClick={() => screen.mutate()}>
              {screen.isPending ? 'Starting…' : 'Screen company now'}
            </Button>
          ) : null}
          <CompanyScreeningReportButton customerId={customerId} />
        </Stack>
      </Stack>

      {screening.isLoading ? (
        <Box sx={{ py: 2 }}><CircularProgress size={20} /></Box>
      ) : screening.isError ? (
        <Alert severity="error" sx={{ mt: 2 }}>Could not load the company screening.</Alert>
      ) : shown === null ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          The company has not been screened yet. Screening starts when the business application is submitted.
        </Alert>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip size="small" color={shown.color} label={shown.label} data-testid="company-screening-state" />
            {state === 'MATCHES_FOUND' ? (
              <Typography variant="body2">{data?.matchCount ?? 0} possible match{data?.matchCount === 1 ? '' : 'es'} to review</Typography>
            ) : null}
            {data?.screenedAt ? (
              <Typography variant="caption" color="text.secondary">
                {data.rescreenRefused ? 'Provider check of ' : 'Screened '}{new Date(data.screenedAt).toLocaleString(APP_LOCALE)}
              </Typography>
            ) : data?.startedAt ? (
              <Typography variant="caption" color="text.secondary">
                Requested {new Date(data.startedAt).toLocaleString(APP_LOCALE)}
              </Typography>
            ) : null}
          </Stack>
          {data?.rescreenRefused ? (
            <Alert severity="info" data-testid="company-screening-refused">
              This is the provider’s existing check{data.screenedAt ? ` from ${new Date(data.screenedAt).toLocaleDateString(APP_LOCALE)}` : ''}
              {data.requestId !== null ? ` (request #${String(data.requestId)})` : ''}. The latest re-screen was refused because
              that check is still open at the provider, so no newer result exists yet.
            </Alert>
          ) : null}
          {state === 'FAILED' && data?.failureReason ? (
            <Alert severity="warning">{FAILURE[data.failureReason] ?? data.failureReason}</Alert>
          ) : null}
          {data?.overallClassification ? (
            <Typography variant="caption" color="text.secondary">
              Whole screening (company and its people): {data.overallClassification.replace(/_/g, ' ').toLowerCase()}
            </Typography>
          ) : null}
          {data?.report ? (
            <Typography variant="body2" data-testid="company-screening-filed">
              Report filed in documents as “{data.report.filename}” on {new Date(data.report.filedAt).toLocaleDateString(APP_LOCALE)}.
            </Typography>
          ) : state === 'CLEAR' || state === 'MATCHES_FOUND' ? (
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" color="text.secondary">
                The report is filed in the client’s documents automatically within a few minutes.
              </Typography>
              {canFile ? (
                <Button size="small" variant="outlined" disabled={file.isPending} onClick={() => file.mutate()}>
                  {file.isPending ? 'Filing…' : 'File report now'}
                </Button>
              ) : null}
            </Stack>
          ) : null}
          {message !== null ? <Typography variant="caption" role="status">{message}</Typography> : null}
        </Stack>
      )}
    </Paper>
  );
}
